import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { toggleFullscreenFilter } from '@gumtree/shared/src/reducers/fullscreen-filter';

import { StyledContent, StyledHeader, StyledTitle, StyledList, StyledIcon } from './index.style';
import Ancestors from './components/ancestors';
import Descendents from './components/descendents';
import { Filter, FilterData, FiltersDictionary } from '../../../types';
import { CUSTOM_FILTERS } from '../../shared/constants';
import { updateSearchResultAbundance } from '../../../reducers/search-result-abundance';
import { fetchAbundance } from '../../../get-data/fetch/fetch-abundance';
import { updateFiltersDictionary } from '../../../reducers/filters-dictionary';
import { CategoriesFilter } from '../../../get-data/transform/category-filter';
import { updateCategoriesFilter } from '../../../reducers/categories-filter';
import { FilterProcessor } from '../../filters/util';
import { SrpState, SrpUseDispatch } from '../../../reducers';
import { setFilterThunk } from '../../../reducers/current-filter-states';

export const createOnCategorySelectHandler =
    (dispatch: SrpUseDispatch, currentFilterStates: Filter) =>
    (value: string, abundance?: number) => {
        dispatch(setFilterThunk({ key: CUSTOM_FILTERS.CATEGORIES, value }));

        if (abundance) {
            dispatch(updateSearchResultAbundance(abundance));
        }

        fetchAbundance(
            CUSTOM_FILTERS.CATEGORIES,
            {
                ...currentFilterStates,
                [CUSTOM_FILTERS.CATEGORIES]: value,
            },
            (data: FilterData) => {
                dispatch(
                    updateFiltersDictionary(
                        {
                            [CUSTOM_FILTERS.CATEGORIES]: data,
                        } as FiltersDictionary,
                        { l1Category: undefined, l2Category: undefined, l3Category: undefined }
                    )
                );
                const newCategoriesFilter: CategoriesFilter = {
                    selectedCategorySeoDisplayName: data.parents.at(-1)?.label || '',
                    ancestorCategories: data.parents,
                    descendentCategories: data.values,
                };
                dispatch(updateCategoriesFilter(newCategoriesFilter));
            },
            (error) => {
                 
                console.error(error);
            }
        );
    };

export const extractFromProps = ({
    ancestorCategories,
    descendentCategories,
    selectedCategorySeoDisplayName,
    currentFilterStates,
    dispatch,
}: CategoriesFilter & {
    currentFilterStates: Filter;
    dispatch: any;
}) => {
    const ancestorCategoriesProcessed = new FilterProcessor(ancestorCategories)
        .markAsChecked(selectedCategorySeoDisplayName)
        .getItems();

    const descendentCategoriesProcessed = new FilterProcessor(descendentCategories)
        .markAsChecked(selectedCategorySeoDisplayName)
        .getItems();

    const onCategorySelect = createOnCategorySelectHandler(dispatch, currentFilterStates);

    const handleToggleCategories = () => {
        dispatch(toggleFullscreenFilter());
        onCategorySelect(currentFilterStates.search_category as string);
    };

    return {
        ancestorCategoriesProcessed,
        descendentCategoriesProcessed,
        onCategorySelect,
        handleToggleCategories,
    };
};

const Categories = () => {
    const dispatch = useDispatch() as SrpUseDispatch;

    const {
        resultsPage: { currentFilterStates, categoriesFilter, fullscreenFilter },
    } = useSelector((data: SrpState): SrpState => data);

    const { ancestorCategories, descendentCategories, selectedCategorySeoDisplayName } =
        categoriesFilter;

    const isCategoriesOpened = fullscreenFilter.isActive;

    const {
        ancestorCategoriesProcessed,
        descendentCategoriesProcessed,
        onCategorySelect,
        handleToggleCategories,
    } = extractFromProps({
        ancestorCategories,
        descendentCategories,
        selectedCategorySeoDisplayName,
        currentFilterStates,
        dispatch,
    });

    return (
        <StyledContent isOpen={isCategoriesOpened}>
            <StyledHeader>
                <StyledIcon type="chevron-l" onClick={handleToggleCategories} />
                <StyledTitle>Categories</StyledTitle>
                <StyledIcon type="clear" onClick={handleToggleCategories} />
            </StyledHeader>
            <StyledList>
                <Ancestors
                    categories={ancestorCategoriesProcessed}
                    onCategorySelect={onCategorySelect}
                />
                <Descendents
                    categories={descendentCategoriesProcessed}
                    onCategorySelect={onCategorySelect}
                />
            </StyledList>
        </StyledContent>
    );
};

export default Categories;
